'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Page-basis control over the top bar. The dashboard layout owns the AppHeader,
 * so pages can't pass it props — instead a page renders `<AppHeaderConfig …/>`
 * and the header reads the overrides from this context (applied while the page
 * is mounted, restored on unmount).
 *
 * This is the single lane for header configuration — there is no route list
 * in the header itself; each page declares what it needs.
 */
export interface AppHeaderOverrides {
  /** Hide the global period picker — for pages with their own time semantics
   * (the home is cycle-scoped; /metas, /investments, /budget, etc. are
   * date-insensitive). */
  hidePeriodPicker?: boolean;
}

interface AppHeaderContextValue {
  overrides: AppHeaderOverrides;
  setOverrides: (overrides: AppHeaderOverrides) => void;
}

const AppHeaderContext = createContext<AppHeaderContextValue | null>(null);

export function AppHeaderProvider({ children }: { children: ReactNode }): ReactNode {
  const [overrides, setOverrides] = useState<AppHeaderOverrides>({});
  const value = useMemo(() => ({ overrides, setOverrides }), [overrides]);
  return <AppHeaderContext.Provider value={value}>{children}</AppHeaderContext.Provider>;
}

/** Header-side read. Safe without a provider (returns no overrides). */
// eslint-disable-next-line react-refresh/only-export-components
export function useAppHeaderOverrides(): AppHeaderOverrides {
  return useContext(AppHeaderContext)?.overrides ?? {};
}

/**
 * Page-side declaration: render anywhere inside a page to configure the top
 * bar while that page is mounted. Later mounts win; unmounting restores the
 * defaults.
 */
export function AppHeaderConfig({ hidePeriodPicker }: AppHeaderOverrides): ReactNode {
  const setOverrides = useContext(AppHeaderContext)?.setOverrides;

  useEffect(() => {
    if (!setOverrides) return;
    setOverrides({ hidePeriodPicker });
    return () => setOverrides({});
  }, [setOverrides, hidePeriodPicker]);

  return null;
}
